import React from "react";
import { useSelector } from "react-redux";
import { useGetSalesSummaryQuery } from "Services/Admin/DashboardApiSlice";
import { useGetConfigurationByPlantQuery } from "Services/Admin/ConfigurationApiSlice";
import { formatIndianNumber } from "Utilities";
import { RootState } from "State/store";

interface MetricCardProps {
  title: string;
  value: string | number;
  change?: string | null;
  isPositive?: boolean | null;
  waveColor?: string | null;
  hasArrow?: boolean;
}

const MetricCard = ({ title, value, change, isPositive, waveColor, hasArrow }: MetricCardProps) => (
  <div className="bg-white rounded-xl shadow-sm border border-gray-100 relative overflow-hidden flex flex-col items-center justify-center h-28 hover:-translate-y-1 transition-transform duration-200 group">
    <h4 className="text-[12px] font-bold text-[#1B2559] mb-1 z-10 text-center tracking-tight opacity-70 group-hover:opacity-100 transition-opacity uppercase">{title}</h4>
    <div className="flex items-center justify-center gap-1 z-10 w-full px-2">
      <h2 className="text-[16px] sm:text-[18px] lg:text-[20px] font-black tracking-tight whitespace-nowrap text-[#1B2559]" title={String(value)}>
        {value}
      </h2>
      {hasArrow && (
        <span className={`text-[10px] mb-2 shrink-0 ${isPositive ? 'text-emerald-500' : 'text-rose-500'}`}>
          {isPositive ? '▲' : '▼'}
        </span>
      )}
    </div>
    {change && (
      <p className={`text-[12px] font-bold mt-0.5 z-10 ${isPositive ? 'text-emerald-500' : 'text-rose-500'}`}>
        {isPositive ? '+' : ''}{change}
      </p>
    )}
    {waveColor && (
      <div className="absolute bottom-0 left-0 w-full overflow-hidden leading-none z-0">
        <svg className="block w-full h-8" viewBox="0 0 100 20" preserveAspectRatio="none">
          <path d="M0,20 L100,20 L100,8 C70,20 30,0 0,15 Z" fill={waveColor} />
        </svg>
      </div>
    )}
  </div>
);

export const MetricsCards = ({ 
  fromDate, 
  toDate, 
  warehouse, 
  categoryId, 
  productId,
  mockMultiplier = 1 
}: { 
  fromDate: string;
  toDate: string;
  warehouse?: string;
  categoryId?: string;
  productId?: string;
  mockMultiplier?: number 
}) => {
  const plant = process.env.REACT_APP_PLANT || "";
  const adminToken = useSelector((state: RootState) => state.adminlogin.adminToken);
  const { data: config } = useGetConfigurationByPlantQuery(plant, { skip: !adminToken });
  const dec = config?.numberOfDecimal ? parseInt(config.numberOfDecimal) : 2;

  const { data: summaryData, isLoading, isFetching } = useGetSalesSummaryQuery({
    plant, fromDate, toDate, warehouse, categoryId, productId
  }, { pollingInterval: 60000 });

  const stats = summaryData?.results || {};

  // Helper to safely format currency/numbers with controlled decimals
  const formatValue = (val: any) => {
    if (isLoading || isFetching) return "...";
    const num = Number(val) || 0;
    return formatIndianNumber(num, dec);
  };

  const metrics = [
    { 
      title: "Total Revenue", 
      value: `₹${formatValue(stats.totalRevenue)}`, 
      change: null, 
      isPositive: true, 
      hasArrow: false, 
      waveColor: "#FFEDD5" 
    },
    { 
      title: "Total Orders", 
      value: (isLoading || isFetching) ? "..." : formatIndianNumber(stats.totalOrders || 0),
      change: null, 
      isPositive: true, 
      hasArrow: false, 
      waveColor: "#DBEAFE" 
    },
    { 
      title: "Avg. Order Value", 
      value: `₹${formatValue(stats.avgOrderValue)}`, 
      change: null, 
      isPositive: null, 
      hasArrow: false, 
      waveColor: null 
    },
    { 
      title: "Conversion Rate", 
      value: (isLoading || isFetching) ? "..." : `${formatValue(stats.conversionRate)}%`, 
      change: null, 
      isPositive: null, 
      hasArrow: false, 
      waveColor: "#DCFCE7" 
    },
    {
      title: "Repeat Purchase",
      value: (isLoading || isFetching) ? "..." : `${formatValue(stats.repeatPurchase)}%`,
      change: null,
      isPositive: null,
      hasArrow: false,
      waveColor: "#DBEAFE"
    },
  ];

  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-3 xl:grid-cols-5 gap-4 mb-6 px-1">
      {metrics.map((m, i) => <MetricCard key={i} {...m} />)}
    </div>
  );
};